import { EVENT_SITES } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, provide, reactive, toRefs } from 'vue';
import { useRoute } from 'vue-router';

import useIteratively from '@/composables/useIteratively';
import {
  CUSTOM,
  HIDDEN,
  OLAPS_ONLY,
  POPULATION_RULE,
  PRESET_FIELDS_LOOKUP,
  RECOMMENDED,
  SHARING,
} from '@/constants/data_shares';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  HS3_DATA_SOURCE_TYPE,
  HUBSPOT_DATA_SOURCE_TYPE,
  SNOWFLAKE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { useFeedsStore, usePartnersStore, useSourcesStore } from '@/stores';

/** Data sharing state */
export const ProvidedDataSharingState = Symbol('ProvidedDataSharingState');

export default function useDataSharing({ population, partnerId }) {
  const { iteratively } = useIteratively();
  const dataSharingState = reactive({
    addRecommendedFieldsToCustom() {
      dataSharingState.customSelectedFields = presetFieldIds.value.reduce(
        (selectedFields, presetId) => {
          if (!selectedFields.includes(presetId)) selectedFields.push(presetId);
          return selectedFields;
        },
        [...dataSharingState.customSelectedFields],
      );
    },
    allPresetFieldsChecked: computed(() =>
      presetFieldIds.value.every((presetFieldId) =>
        dataSharingState.customSelectedFields.includes(presetFieldId),
      ),
    ),
    allSelectableFields: computed(() => {
      return dataSharingState.filterableSources.reduce((fields, source) => {
        return [...fields, ...source.fields.map((field) => field.id)];
      }, []);
    }),
    customSelectedFields: [],
    filterableSources: computed(
      () => popSourceIdToSourcesMap.value[population?.source_id] || [],
    ),
    isGreenfieldChecked: false,
    popToPopSettings: {},
    selectedPreset: RECOMMENDED,
    trackPresetClicked(preset) {
      iteratively.userClickedDataSharingPreset({
        event_site: EVENT_SITES.SHARING_SETTINGS_MODAL,
        partner_org_id: partnerId?.toString() || '',
        preset_type: preset,
      });
    },
  });

  const feedsStore = useFeedsStore();
  const sourcesStore = useSourcesStore();
  const { popSourceIdToSourcesMap } = storeToRefs(sourcesStore);
  const partnersStore = usePartnersStore();

  const route = useRoute();

  const isNewPop = computed(() => route.query?.newPopulation);

  const isUpload = computed(() =>
    [FILE_UPLOAD_DATA_SOURCE_TYPE, GOOGLE_SHEETS_DATA_SOURCE_TYPE].some(
      (dataSourceType) => popSchema.value?.includes(dataSourceType),
    ),
  );

  // partner level
  const isPartnerType = computed(() => !!partnerId);
  const isPopulationLevelRule = computed(() => {
    const ruleType = population.dataShare.rule_type;
    return !ruleType || ruleType === POPULATION_RULE;
  });

  const partner = computed(() => {
    if (!partnerId) return null;
    return partnersStore.getPartnerOrgById(partnerId);
  });

  const partnersNotUsingDefaults = computed(() => {
    if (isPartnerType.value) return [];
    return population.partnersNotUsingDefaults;
  });

  // copy
  const modalText = computed(() => {
    if (isPartnerType.value) {
      return {
        title: `Sharing Settings for ${partner.value?.name || 'Partner'}`,
        text: `How do you want to share data with ${partner.value?.name || 'this partner'}
      for your ${population.name} population?`,
      };
    }
    return {
      title: `${population.name} - Sharing Default`,
      text: `${population.name} Sharing Default`,
    };
  });

  const sharingText = computed(() => {
    const partnerName = isPartnerType.value
      ? partner.value?.name
      : 'Your partners';
    return {
      [SHARING]: `${isPartnerType.value ? partner.value?.name : 'By default, your partners'} can see the data shared
      with them for all overlaps`,
      [OLAPS_ONLY]: `${partnerName} can see a summary count of overlaps for this Population.
      No specifics about the overlaps, including name or any other data, is shared`,
      [HIDDEN]: `${partnerName} cannot see this population`,
    };
  });

  // presets
  const popSource = computed(() =>
    sourcesStore.getSourceById(population.source_id),
  );
  const popSchema = computed(() => {
    const schema = feedsStore.getFeedById(popSource.value?.feed_id)?.integration
      .type;
    if (schema?.includes(HUBSPOT_DATA_SOURCE_TYPE)) return HS3_DATA_SOURCE_TYPE;
    return schema;
  });

  const presetFields = computed(() => {
    return dataSharingState.filterableSources.reduce((sources, source) => {
      if (!PRESET_FIELDS_LOOKUP[source.mdm_type]) return sources;
      const fields = source.fields.filter((field) =>
        PRESET_FIELDS_LOOKUP[source.mdm_type].includes(field.mdm_property),
      );
      if (!fields.length) return sources;
      return { ...sources, [source.table]: { ...source, fields } };
    }, []);
  });

  const presetFieldIds = computed(() => {
    return Object.keys(presetFields.value).reduce((fields, table) => {
      return [
        ...fields,
        ...presetFields.value[table].fields.map((field) => field.id),
      ];
    }, []);
  });

  function areOnlyPresetFieldsSelected(fields) {
    const presetIdsString = presetFieldIds.value.sort().join('');
    const selectedFieldsString = fields.sort().join('');
    return presetIdsString === selectedFieldsString;
  }

  const sharingPresets = computed(() => {
    const presets = [
      {
        description: 'Customize fields based on our recommendations',
        sourcesAndFields: dataSharingState.customSelectedFields,
        name: 'Customize Fields',
        value: CUSTOM,
        active: dataSharingState.selectedPreset === CUSTOM,
      },
    ];
    if (!isUpload.value) {
      presets.unshift({
        description: 'Required fields for account mapping and co-selling',
        sourcesAndFields: presetFields.value,
        name: 'Recommended',
        value: RECOMMENDED,
        active: dataSharingState.selectedPreset === RECOMMENDED,
      });
    }
    return presets;
  });

  function selectRecommendedFields() {
    const isUploadOrSnowflake =
      isUpload.value || popSchema.value?.includes(SNOWFLAKE_DATA_SOURCE_TYPE);
    if (isUploadOrSnowflake) {
      dataSharingState.selectedPreset = CUSTOM;
      dataSharingState.customSelectedFields = [
        ...dataSharingState.allSelectableFields,
      ];
    } else {
      dataSharingState.selectedPreset = RECOMMENDED;
      dataSharingState.customSelectedFields = [...presetFieldIds.value];
    }
  }

  const dataSharingStateRefs = toRefs(dataSharingState);
  provide(ProvidedDataSharingState, dataSharingStateRefs);
  return {
    areOnlyPresetFieldsSelected,
    ...dataSharingStateRefs,
    isNewPop,
    isPartnerType,
    isPopulationLevelRule,
    isUpload,
    modalText,
    partner,
    partnersNotUsingDefaults,
    popSchema,
    presetFieldIds,
    selectRecommendedFields,
    sharingPresets,
    sharingText,
  };
}
